import React, { useEffect, useState } from "react";
import {
  CircularProgress, Box, Typography, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination, Chip,
  TextField, InputAdornment, FormControl, InputLabel, Select, MenuItem,
} from "@mui/material";
import SearchIcon           from "@mui/icons-material/Search";
import HistoryIcon          from "@mui/icons-material/History";
import PersonIcon           from "@mui/icons-material/Person";
import SettingsIcon         from "@mui/icons-material/Settings";
import PaymentIcon          from "@mui/icons-material/Payment";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import { useSnackbar } from "notistack";
import moment from "moment";
import { get_school_audit_logs_api } from "../../DAL/FlightSchools/FlightSchools";

const getActionIcon = (action) => {
  if (action.includes("student"))                            return <PersonIcon sx={{ fontSize: 16 }} />;
  if (action.includes("billing") || action.includes("payment")) return <PaymentIcon sx={{ fontSize: 16 }} />;
  if (action.includes("setting") || action.includes("branding")) return <SettingsIcon sx={{ fontSize: 16 }} />;
  if (action.includes("impersonate") || action.includes("admin")) return <AdminPanelSettingsIcon sx={{ fontSize: 16 }} />;
  return <HistoryIcon sx={{ fontSize: 16 }} />;
};

const getActionColor = (action) => {
  if (action.includes("create") || action.includes("add"))     return "#16a34a";
  if (action.includes("delete") || action.includes("suspend")) return "#c01e2e";
  if (action.includes("update") || action.includes("renew"))   return "#2563eb";
  if (action.includes("impersonate"))                          return "#7c3aed";
  return "#6b6b6b";
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#ffffff", borderRadius: "9px", color: "#1a1a1a",
    "& fieldset": { borderColor: "rgba(26,26,26,0.16)" },
    "&:hover fieldset": { borderColor: "rgba(26,26,26,0.28)" },
    "&.Mui-focused fieldset": { borderColor: "#c01e2e", borderWidth: "1.5px" },
  },
  "& .MuiInputBase-input": { color: "#1a1a1a" },
};

const thSx = {
  color: "#6b6b6b", fontWeight: 600, fontSize: "11px", textTransform: "uppercase",
  letterSpacing: "0.4px", fontFamily: "'Rubik', sans-serif",
  borderBottom: "1px solid rgba(26,26,26,0.09)", backgroundColor: "#F2EDE4",
};

export default function FlightSchoolAuditLog({ schoolId }) {
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(true);
  const [logs,         setLogs]   = useState([]);
  const [searchQuery,  setSearchQuery]  = useState("");
  const [actionFilter, setActionFilter] = useState("all");
  const [page,         setPage]         = useState(0);
  const [rowsPerPage,  setRowsPerPage]  = useState(25);

  useEffect(() => { loadAuditLogs(); }, [schoolId]);

  const loadAuditLogs = async () => {
    try {
      const r = await get_school_audit_logs_api(schoolId);
      if (r.code === 200) setLogs(r.audit_logs || []);
      else enqueueSnackbar(r.message || "Failed to load audit logs", { variant: "error" });
    } catch { enqueueSnackbar("Error loading audit logs", { variant: "error" }); }
    finally { setIsLoading(false); }
  };

  const filteredLogs = logs.filter((log) => {
    const s = searchQuery.toLowerCase();
    const matchesSearch = !searchQuery || log.action?.toLowerCase().includes(s) || log.performed_by?.name?.toLowerCase().includes(s) || log.details?.toLowerCase().includes(s);
    const matchesAction = actionFilter === "all" || log.action?.toLowerCase().includes(actionFilter);
    return matchesSearch && matchesAction;
  });

  const actionTypes = [...new Set(logs.map((l) => l.action?.split("_")[0]).filter(Boolean))];

  if (isLoading) return <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress sx={{ color: "#c01e2e" }} /></Box>;

  return (
    <Box>
      {/* Filters */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <TextField size="small" placeholder="Search logs…" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ ...inputSx, minWidth: 280 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: "#aaa", fontSize: 18 }} /></InputAdornment> }} />

        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel sx={{ color: "#aaa", fontSize: "14px", "&.Mui-focused": { color: "#c01e2e" } }}>Action Type</InputLabel>
          <Select value={actionFilter} label="Action Type" onChange={(e) => setActionFilter(e.target.value)}
            sx={{ backgroundColor: "#ffffff", borderRadius: "9px", color: "#1a1a1a",
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(26,26,26,0.16)" },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(26,26,26,0.28)" },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#c01e2e", borderWidth: "1.5px" },
              "& .MuiSvgIcon-root": { color: "#c01e2e" } }}>
            <MenuItem value="all">All</MenuItem>
            {actionTypes.map((t) => <MenuItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</MenuItem>)}
          </Select>
        </FormControl>

        <Box sx={{ ml: "auto", display: "flex", alignItems: "center" }}>
          <Typography sx={{ color: "#aaa", fontSize: "13px", fontFamily: "'Rubik', sans-serif" }}>
            {filteredLogs.length} log entries
          </Typography>
        </Box>
      </Box>

      {filteredLogs.length > 0 ? (
        <>
          <TableContainer component={Paper} sx={{ backgroundColor: "transparent", boxShadow: "none" }}>
            <Table>
              <TableHead>
                <TableRow>
                  {["Timestamp", "Action", "Performed By", "Details", "IP Address"].map((h) => (
                    <TableCell key={h} sx={thSx}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredLogs.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((log, i) => (
                  <TableRow key={log._id || i} sx={{ "&:hover": { backgroundColor: "#F7F4EF" }, "& td": { borderBottom: "1px solid rgba(26,26,26,0.06)" } }}>
                    <TableCell>
                      <Typography sx={{ color: "#1a1a1a", fontSize: "13px", fontFamily: "'Rubik', sans-serif" }}>{moment(log.timestamp).format("DD MMM YYYY")}</Typography>
                      <Typography sx={{ color: "#aaa", fontSize: "11px", fontFamily: "'Rubik', sans-serif" }}>{moment(log.timestamp).format("HH:mm:ss")}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip icon={getActionIcon(log.action)} label={log.action?.replace(/_/g, " ")} size="small"
                        sx={{ backgroundColor: `${getActionColor(log.action)}18`, color: getActionColor(log.action), borderRadius: "7px", fontSize: "11px", textTransform: "capitalize", fontFamily: "'Rubik', sans-serif", "& .MuiChip-icon": { color: getActionColor(log.action) } }} />
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ color: "#1a1a1a", fontSize: "13px", fontFamily: "'Rubik', sans-serif" }}>{log.performed_by?.name || "System"}</Typography>
                      {log.performed_by?.email && <Typography sx={{ color: "#aaa", fontSize: "11px", fontFamily: "'Rubik', sans-serif" }}>{log.performed_by.email}</Typography>}
                      {log.performed_by?.is_super_admin && (
                        <Chip label="Super Admin" size="small" sx={{ mt: 0.5, backgroundColor: "rgba(124,58,237,0.1)", color: "#7c3aed", fontSize: "10px", height: "18px", fontFamily: "'Rubik', sans-serif" }} />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ color: "#1a1a1a", fontSize: "13px", wordBreak: "break-word", fontFamily: "'Rubik', sans-serif" }}>{log.details || "—"}</Typography>
                    </TableCell>
                    <TableCell sx={{ color: "#aaa", fontSize: "12px", fontFamily: "'Rubik', sans-serif" }}>{log.ip_address || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination component="div" count={filteredLogs.length} page={page}
            onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            sx={{ color: "#6b6b6b", fontFamily: "'Rubik', sans-serif",
              "& .MuiTablePagination-selectIcon": { color: "#c01e2e" },
              "& .MuiTablePagination-actions button": { color: "#6b6b6b" } }} />
        </>
      ) : (
        <Paper sx={{ p: 6, backgroundColor: "#ffffff", border: "1px solid rgba(26,26,26,0.09)", borderRadius: "12px", textAlign: "center", boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
          <HistoryIcon sx={{ fontSize: 44, color: "#ddd", mb: 2 }} />
          <Typography sx={{ color: "#aaa", fontSize: "15px", fontFamily: "'Rubik', sans-serif" }}>No audit logs found</Typography>
          <Typography sx={{ color: "#ccc", fontSize: "13px", mt: 0.5, fontFamily: "'Rubik', sans-serif" }}>Admin actions will appear here once they occur</Typography>
        </Paper>
      )}
    </Box>
  );
}
